import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../auth/store';
import {
  useMerchant, useMerchantApiKey, useMerchantUsers,
  useGenerateApiKey, useRevokeApiKey, useToggleMerchantStatus,
  useMerchantApiKeysHistory, useMerchantMetrics, useMerchantPauseStatus, useToggleMerchantPause,
  useDeleteMerchant, useResendInvite, useUpdateMerchantUser,
} from './hooks/useMerchants';
import type { MerchantUser } from './types';
import { CONTRACT_TYPE_LABELS, INVOICING_CADENCE_LABELS } from './types';
import { useSolutions } from '../metadata/hooks/useMetadata';
import { usePermissions, Permission } from '../../shared/hooks/usePermissions';
import { useTenantStore } from '../../shared/stores/useTenantStore';
import Button from '../../shared/components/Button';
import { EditMerchantForm } from './index';
import { DomainFieldBuilder } from '../field-config/components/DomainFieldBuilder';
import { MerchantPayoutDestination } from '../payout-destinations/MerchantPayoutDestination';
import { MerchantSettlements } from '../settlements/MerchantSettlements';
import MerchantSessionRecordings from './MerchantSessionRecordings';
import { MerchantStorefront } from '../storefront-onboarding/components/MerchantStorefront';
import { BrandAssetsPanel } from '../storefront-onboarding/components/BrandAssetsPanel';
import { CustomDomainPanel } from '../storefront-onboarding/components/CustomDomainPanel';
import { BillingSettingsPanel } from '../invoices/BillingSettingsPanel';
import { MerchantReturnPolicyPanel } from '../returns-policy/components/MerchantReturnPolicyPanel';
import { MerchantShippingZonesPanel } from '../shipping-zones/components/MerchantShippingZonesPanel';
import { MerchantInstagramPanel } from '../instagram/components/MerchantInstagramPanel';

function formatDate(d: string | null) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

// Includes the time (unlike formatDate) so a just-clicked "Resend Invite" is visibly confirmed,
// not just a same-day date that looks unchanged from the last send.
function formatDateTime(d: string | null) {
  if (!d) return '—';
  return new Date(d).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

// ─── Show-once API key modal ──────────────────────────────────────────────────

function ApiKeyModal({ apiKey, onClose }: { apiKey: string; onClose: () => void }) {
  const [copied, setCopied] = useState(false);

  const copy = () => {
    navigator.clipboard.writeText(apiKey);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-card rounded-lg w-full max-w-lg">
        <div className="p-6 border-b">
          <h2 className="text-xl font-bold text-foreground">API Key Generated</h2>
        </div>
        <div className="p-6 space-y-4">
          <div className="bg-amber-50 border border-amber-200 rounded-md p-3 text-sm text-amber-800">
            Copy this key now — it will never be shown again.
          </div>
          <div className="flex gap-2">
            <code className="flex-1 bg-muted rounded-md px-3 py-2 text-sm font-mono break-all">{apiKey}</code>
            <button
              onClick={copy}
              className="px-3 py-2 border border-border rounded-md text-sm hover:bg-muted whitespace-nowrap"
            >
              {copied ? 'Copied!' : 'Copy'}
            </button>
          </div>
          <Button onClick={onClose} className="w-full">Done</Button>
        </div>
      </div>
    </div>
  );
}

// ─── Edit merchant user modal ─────────────────────────────────────────────────

const userFieldCls = 'w-full bg-background text-foreground placeholder:text-muted-foreground border border-border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring';

function EditUserModal({
  user, merchantId, onClose,
}: { user: MerchantUser; merchantId: string; onClose: () => void }) {
  const updateUser = useUpdateMerchantUser(merchantId);
  const [fullName, setFullName] = useState(user.fullName);
  const [email, setEmail] = useState(user.email);
  const [phoneNumber, setPhoneNumber] = useState(user.phoneNumber ?? '');
  // The login email drives password-reset delivery, so the backend only permits changing it
  // for a PENDING, unclaimed invite (never verified, never completed a profile). Mirror that
  // here so the field is editable exactly when the change will be accepted.
  const canEditEmail = !user.isEmailVerified && !user.isProfileCompleted;

  // Only send fields that actually changed — the backend rejects a no-op payload.
  const changed: Record<string, string> = {};
  if (fullName.trim() !== user.fullName) changed.fullName = fullName.trim();
  if (canEditEmail && email.trim().toLowerCase() !== user.email.toLowerCase()) changed.email = email.trim();
  if (phoneNumber.trim() !== (user.phoneNumber ?? '')) changed.phoneNumber = phoneNumber.trim();
  const hasChanges = Object.keys(changed).length > 0;

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!hasChanges) return;
    updateUser.mutate({ userId: user.id, data: changed }, { onSuccess: onClose });
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <form onSubmit={submit} className="bg-card rounded-lg w-full max-w-md">
        <div className="p-6 border-b">
          <h2 className="text-xl font-bold text-foreground">Edit User</h2>
          {canEditEmail && (
            <p className="text-xs text-muted-foreground mt-1">
              This user hasn't claimed their invite yet. Changing the email updates where the
              invite link and future correspondence are sent — re-send the invite afterwards.
            </p>
          )}
        </div>
        <div className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">Full name</label>
            <input className={userFieldCls} value={fullName} onChange={(e) => setFullName(e.target.value)} minLength={2} required />
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">Email</label>
            <input
              className={`${userFieldCls} disabled:opacity-60 disabled:cursor-not-allowed`}
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={!canEditEmail}
              required
            />
            {!canEditEmail && (
              <p className="text-xs text-muted-foreground mt-1">
                Email can only be changed before the invite is claimed.
              </p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">
              Phone <span className="text-muted-foreground font-normal">(optional)</span>
            </label>
            <input className={userFieldCls} value={phoneNumber} onChange={(e) => setPhoneNumber(e.target.value)} maxLength={30} placeholder="+44 7700 900000" />
          </div>
        </div>
        <div className="p-6 border-t flex gap-3 justify-end">
          <button type="button" onClick={onClose} className="px-4 py-2 border border-border rounded-md text-sm text-foreground hover:bg-muted">
            Cancel
          </button>
          <Button type="submit" loading={updateUser.isPending} disabled={!hasChanges || updateUser.isPending}>
            Save changes
          </Button>
        </div>
      </form>
    </div>
  );
}

// ─── Main detail component ────────────────────────────────────────────────────

export default function MerchantDetail({ id }: { id: string }) {
  const navigate = useNavigate();
  const { data: merchant, isLoading, error } = useMerchant(id);
  const { data: apiKey } = useMerchantApiKey(id);
  const { data: users = [] } = useMerchantUsers(id);
  const { data: apiKeysHistory = [] } = useMerchantApiKeysHistory(id);
  const { data: metrics, isLoading: metricsLoading, error: metricsError } = useMerchantMetrics(id);
  const { data: pauseStatus } = useMerchantPauseStatus(id);
  const { data: solutions = [] } = useSolutions();
  const togglePause = useToggleMerchantPause(id);

  const generateKey = useGenerateApiKey(id);
  const revokeKey = useRevokeApiKey(id);
  const toggleStatus = useToggleMerchantStatus();
  const { hasPermission } = usePermissions();
  const setActiveMerchant = useTenantStore((s) => s.setActiveMerchant);
  const setSelectedTenantId = useAuthStore((s) => s.setSelectedTenantId);

  // Merchant-scoped tools (Booking Services, Custom Orders, Metadata…) live as their own
  // pages that read the "current merchant" from a store. A COLLECTIVE admin reaches them
  // from HERE — so pin this merchant into BOTH stores (some pages read useTenantStore's
  // activeMerchant, others read useAuthStore's selectedTenantId) before navigating.
  const manageForMerchant = (path: string) => {
    setActiveMerchant(merchant ?? null);
    setSelectedTenantId(merchant?.id ?? null);
    navigate(path);
  };

  const deleteMerchant = useDeleteMerchant();
  const resendInvite = useResendInvite(id);

  const [showEdit, setShowEdit] = useState(false);
  const [newKey, setNewKey] = useState<string | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showStatusConfirm, setShowStatusConfirm] = useState(false);
  const [editingUser, setEditingUser] = useState<MerchantUser | null>(null);

  // PE-2249 — `/merchants/:id` is a single Route, so React Router reuses this SAME component
  // instance across a merchant-to-merchant navigation (only `id` changes; no remount). The
  // server data refreshes fine on its own (every query above is keyed on `id`), but this
  // component's own local UI state does not reset itself. Concretely: generate an API key for
  // merchant A (opens the "API key generated" modal), then navigate to merchant B — without
  // this, the stale modal (and any other open dialog) stays up, showing merchant A's data over
  // what looks like an unrefreshed page. Reset all of it whenever the merchant changes.
  useEffect(() => {
    setShowEdit(false);
    setNewKey(null);
    setShowDeleteConfirm(false);
    setShowStatusConfirm(false);
    setEditingUser(null);
  }, [id]);

  const canUpdate = hasPermission(Permission.MERCHANT_UPDATE);
  const canUpdateUser = hasPermission(Permission.USER_UPDATE);
  const canDeactivate = hasPermission(Permission.MERCHANT_DEACTIVATE);
  const canDelete = hasPermission(Permission.MERCHANT_DELETE);
  const canManageApiKey = hasPermission(Permission.API_KEY_CREATE);
  const canRevokeApiKey = hasPermission(Permission.API_KEY_REVOKE);
  const canViewPayoutDestination = hasPermission(Permission.PAYOUT_DESTINATION_VIEW);
  const canViewSettlements = hasPermission(Permission.SETTLEMENT_VIEW);
  const canViewSessionRecordings = hasPermission(Permission.OBSERVABILITY_SESSION_RECORDINGS_VIEW);

  const onboardingComplete = merchant ? !merchant.invitePending : false;

  const handleGenerate = async () => {
    const result = await generateKey.mutateAsync();
    setNewKey(result.apiKey);
  };

  if (isLoading) return <div className="py-12 text-center text-foreground">Loading…</div>;
  if (error || !merchant) return <div className="py-12 text-center text-red-500">Merchant not found.</div>;

  const solutionName = solutions.find((s) => s.slug === merchant.solutionId)?.name ?? merchant.solutionId;

  return (
    <div className="space-y-6 py-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/merchants')} className="text-foreground hover:text-foreground text-sm underline">
            ← Merchants
          </button>
          <span className="text-gray-300">/</span>
          <h1 className="text-2xl font-bold text-foreground">{merchant.name}</h1>
          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${merchant.active ? 'bg-green-100 text-green-800' : 'bg-muted text-muted-foreground'}`}>
            {merchant.active ? 'Active' : 'Inactive'}
          </span>
        </div>
        <div className="flex gap-2">
          {canUpdate && <Button onClick={() => setShowEdit(true)}>Edit</Button>}
          {canDeactivate && (
            <button
              onClick={() => setShowStatusConfirm(true)}
              disabled={toggleStatus.isPending}
              className={`px-4 py-2 rounded-md text-sm font-medium border ${merchant.active ? 'border-red-300 text-red-600 hover:bg-red-50' : 'border-green-300 text-green-700 hover:bg-green-50'}`}
            >
              {merchant.active ? 'Deactivate' : 'Activate'}
            </button>
          )}
          {canDelete && !merchant.active && (
            <button
              onClick={() => setShowDeleteConfirm(true)}
              className="px-4 py-2 rounded-md text-sm font-medium border border-red-500 bg-red-500 text-white hover:bg-red-600"
            >
              Delete Permanently
            </button>
          )}
        </div>
      </div>

      {/* Metrics Panel */}
      {metricsError ? (
        <div className="bg-card rounded-lg border border-border p-4 shadow-sm text-sm text-red-500">
          Failed to load activity metrics.
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <div className="bg-card rounded-lg border border-border p-4 shadow-sm">
            <div className="text-sm text-foreground">Total Revenue</div>
            <div className="text-2xl font-bold text-foreground mt-1">
              {metricsLoading ? '…' : metrics ? `₦${metrics.totalRevenue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '—'}
            </div>
          </div>
          <div className="bg-card rounded-lg border border-border p-4 shadow-sm">
            <div className="text-sm text-foreground">Total Orders</div>
            <div className="text-2xl font-bold text-foreground mt-1">
              {metricsLoading ? '…' : metrics ? metrics.totalOrders.toLocaleString() : '—'}
            </div>
          </div>
          <div className="bg-card rounded-lg border border-border p-4 shadow-sm">
            <div className="text-sm text-foreground">Number of Products</div>
            <div className="text-2xl font-bold text-foreground mt-1">
              {metricsLoading ? '…' : metrics ? metrics.productCount.toLocaleString() : '—'}
            </div>
          </div>
          <div className="bg-card rounded-lg border border-border p-4 shadow-sm">
            <div className="text-sm text-foreground">Merchant Users</div>
            <div className="text-2xl font-bold text-foreground mt-1">
              {users ? users.length.toLocaleString() : '—'}
            </div>
          </div>
          <div className="bg-card rounded-lg border border-border p-4 shadow-sm">
            <div className="text-sm text-foreground">Unique Customers</div>
            <div className="text-2xl font-bold text-foreground mt-1">
              {metricsLoading ? '…' : metrics ? metrics.uniqueCustomerCount.toLocaleString() : '—'}
            </div>
          </div>
        </div>
      )}

      {/* Details */}
      <div className="bg-card rounded-lg border border-border p-6">
        <h2 className="text-base font-semibold text-foreground mb-4">Details</h2>
        <dl className="grid grid-cols-2 gap-x-8 gap-y-3 text-sm">
          <div>
            <dt className="text-foreground">Solution</dt>
            <dd className="font-medium text-foreground">{solutionName}</dd>
          </div>
          <div>
            <dt className="text-foreground">Contract Type</dt>
            <dd className="font-medium text-foreground">{CONTRACT_TYPE_LABELS[merchant.contractType] ?? merchant.contractType ?? '—'}</dd>
          </div>
          <div>
            <dt className="text-foreground">Invoicing</dt>
            <dd className="font-medium text-foreground">{INVOICING_CADENCE_LABELS[merchant.invoicingCadence] ?? merchant.invoicingCadence ?? '—'}</dd>
          </div>
          <div>
            <dt className="text-foreground">Commission</dt>
            <dd className="font-medium text-foreground">
              {merchant.contractType && merchant.contractType !== 'COMMISSION'
                ? '—'
                : `${(merchant.commissionCharge * 100).toFixed(1)}%`}
            </dd>
          </div>
          <div>
            <dt className="text-foreground">BPM Officer</dt>
            <dd className="font-medium text-foreground">{merchant.bpmOfficer?.fullName ?? '—'}</dd>
          </div>
          <div>
            <dt className="text-foreground">Liaison</dt>
            <dd className="font-medium text-foreground">{merchant.liaison?.fullName ?? '—'}</dd>
          </div>
          <div>
            <dt className="text-foreground">Contract Start</dt>
            <dd className="font-medium text-foreground">{formatDate(merchant.contractStartDate)}</dd>
          </div>
          <div>
            <dt className="text-foreground">Contract End</dt>
            <dd className="font-medium text-foreground">{formatDate(merchant.contractEndDate)}</dd>
          </div>
          <div>
            <dt className="text-foreground">Next Review</dt>
            <dd className="font-medium text-foreground">{formatDate(merchant.nextContractReviewDate)}</dd>
          </div>
          <div>
            <dt className="text-foreground">Industries</dt>
            <dd className="font-medium text-foreground">{merchant.industries.map((i) => i.industry.name).join(', ') || '—'}</dd>
          </div>
          <div>
            <dt className="text-foreground">Business Categories</dt>
            <dd className="font-medium text-foreground">{merchant.businessCategories.map((c) => c.businessCategory.name).join(', ') || '—'}</dd>
          </div>
          {merchant.partnerSuccessBlueprintUrl && (
            <div>
              <dt className="text-foreground">Partner Blueprint</dt>
              <dd className="font-medium text-foreground">
                <a href={merchant.partnerSuccessBlueprintUrl} target="_blank" rel="noopener noreferrer"
                  className="text-blue-600 hover:underline truncate block max-w-xs">
                  {merchant.partnerSuccessBlueprintUrl}
                </a>
              </dd>
            </div>
          )}
          <div className="col-span-2">
            <dt className="text-foreground">Company Address</dt>
            <dd className="font-medium text-foreground whitespace-pre-wrap">{merchant.companyAddress || '—'}</dd>
          </div>
        </dl>
      </div>

      {/* ── Storefront Onboarding ────────────────────────────────────────────────
          The guided standup sequence for this merchant's personalised storefront:
          1. Standup   — tenant · infra · deploy (dispatch + go-live below)
          2. Brand assets — upload logo/hero/favicon to GCS, feed the storefront config
          3. API key   — issued in the API Key section further down
          Provisioning is dispatched here or from the "Provision infra" action on the list. */}
      <div className="space-y-4">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
          Storefront Onboarding
        </h2>
        <MerchantStorefront slug={merchant.slug} canProvision={hasPermission(Permission.MERCHANT_CREATE)} />
        {canUpdate && <CustomDomainPanel merchantId={merchant.id} />}
        {canUpdate && <BrandAssetsPanel merchantId={merchant.id} solutionId={merchant.solutionId} />}
      </div>

      {/* Billing Settings (PE-2105): invoicing cadence, price-expiry window, late fee rule */}
      {canUpdate && <BillingSettingsPanel merchant={merchant} canUpdate={canUpdate} />}

      {/* Returns & exchanges policy (PE-2163) — read-only; only the merchant can edit it. */}
      <MerchantReturnPolicyPanel merchantId={merchant.id} />

      {/* Shipping zones (PE-2172/PE-2173) — read-only; see the panel's note on why it can
          only show live data while this merchant is the globally-selected one. */}
      <MerchantShippingZonesPanel merchantId={merchant.id} />

      {/* Instagram feed (PE-2178) — read-only, plus the Reset support action. The merchant
          authorises the connection themselves on Instagram, and watchman's `me/instagram/*`
          routes resolve the merchant from the JWT, so there is no TCS-side connect to offer. */}
      <MerchantInstagramPanel merchantId={merchant.id} />

      {/* Payout Destination (split-at-source) */}
      {canViewPayoutDestination && (
        <MerchantPayoutDestination tenantId={merchant.id} businessName={merchant.name} />
      )}

      {/* Settlements */}
      {canViewSettlements && <MerchantSettlements tenantId={merchant.id} />}

      {/* Session recordings (Microsoft Clarity) */}
      {canViewSessionRecordings && (
        <MerchantSessionRecordings projectId={merchant.clarityProjectId} merchantName={merchant.name} />
      )}

      {/* Operational Pause — TCS-admin action (per the demo tracker's Actor column), gated on the same
          MERCHANT_UPDATE permission the toggle itself requires, not on the MERCHANT_ADMIN role. Was
          previously isMerchantAdmin-only, which meant no role could ever both see and use this panel:
          MERCHANT_ADMIN saw it but always failed canUpdate; COLLECTIVE_ADMIN held canUpdate but never
          saw the panel at all. PE-2263. */}
      {canUpdate && (
        <div className="bg-card rounded-lg border border-border p-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-base font-semibold text-foreground">Operational Pause</h2>
              <p className="text-sm text-foreground mt-1">
                Temporarily pause all customer checkout and cart additions for this merchant.
              </p>
            </div>
            <div className="flex items-center gap-3">
              <span className={`text-sm font-medium ${pauseStatus?.isOrdersPaused ? 'text-amber-600' : 'text-green-600'}`}>
                {pauseStatus?.isOrdersPaused ? 'Paused' : 'Active'}
              </span>
              <button
                onClick={() => togglePause.mutate(!pauseStatus?.isOrdersPaused)}
                disabled={togglePause.isPending || !canUpdate}
                className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-black focus:ring-offset-2 ${pauseStatus?.isOrdersPaused ? 'bg-amber-500' : 'bg-muted'} ${!canUpdate ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                <span
                  className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-card shadow ring-0 transition duration-200 ease-in-out ${pauseStatus?.isOrdersPaused ? 'translate-x-5' : 'translate-x-0'}`}
                />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* API Key */}
      <div className="bg-card rounded-lg border border-border p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-base font-semibold text-foreground">API Key</h2>
            {!onboardingComplete && (
              <p className="text-xs text-amber-600 mt-0.5">Merchant must complete onboarding before an API key can be issued.</p>
            )}
          </div>
          <div className="flex gap-2">
            {canManageApiKey && (
              <Button
                onClick={handleGenerate}
                loading={generateKey.isPending}
                disabled={!onboardingComplete}
                title={!onboardingComplete ? 'Merchant has not completed onboarding' : undefined}
              >
                {apiKey ? 'Rotate Key' : 'Generate Key'}
              </Button>
            )}
            {canRevokeApiKey && apiKey && (
              <button
                onClick={() => revokeKey.mutate()}
                disabled={revokeKey.isPending || !onboardingComplete}
                className="px-3 py-2 border border-red-300 text-red-600 rounded-md text-sm hover:bg-red-50 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Revoke
              </button>
            )}
          </div>
        </div>

        {apiKey ? (
          <dl className="grid grid-cols-3 gap-x-8 gap-y-3 text-sm">
            <div>
              <dt className="text-foreground">Status</dt>
              <dd>
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                  {apiKey.status}
                </span>
              </dd>
            </div>
            <div>
              <dt className="text-foreground">Created</dt>
              <dd className="font-medium text-foreground">{formatDate(apiKey.createdAt)}</dd>
            </div>
            <div>
              <dt className="text-foreground">Last Used</dt>
              <dd className="font-medium text-foreground">{formatDate(apiKey.lastUsedAt)}</dd>
            </div>
          </dl>
        ) : (
          <p className="text-sm text-foreground">No active API key. Generate one to allow this merchant to access the commerce API.</p>
        )}
      </div>

      {/* Revoked API Keys History */}
      <div className="bg-card rounded-lg border border-border p-6">
        <h2 className="text-base font-semibold text-foreground mb-4">Revoked API Keys History</h2>
        {apiKeysHistory.filter(k => k.status === 'REVOKED' || k.revokedAt).length === 0 ? (
          <p className="text-sm text-foreground">No revoked API keys.</p>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border">
                <th className="text-left py-2 font-semibold text-foreground">Key</th>
                <th className="text-left py-2 font-semibold text-foreground">Status</th>
                <th className="text-left py-2 font-semibold text-foreground">Created</th>
                <th className="text-left py-2 font-semibold text-foreground">Revoked Date</th>
                <th className="text-left py-2 font-semibold text-foreground">Revoked By</th>
              </tr>
            </thead>
            <tbody>
              {apiKeysHistory.filter(k => k.status === 'REVOKED' || k.revokedAt).map((key) => (
                <tr key={key.id} className="border-b border-border">
                  <td className="py-2 font-mono text-foreground">{key.key}</td>
                  <td className="py-2">
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                      {key.status}
                    </span>
                  </td>
                  <td className="py-2 text-foreground">{formatDate(key.createdAt)}</td>
                  <td className="py-2 text-foreground">{formatDate(key.revokedAt)}</td>
                  <td className="py-2 text-foreground">
                    {key.revokedBy ? `${key.revokedBy.fullName} (${key.revokedBy.email})` : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Users */}
      <div className="bg-card rounded-lg border border-border p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-base font-semibold text-foreground">Users ({users.length})</h2>
          {canUpdate && users.some((u) => !u.isProfileCompleted) && (
            <div className="flex items-center gap-3">
              {merchant.lastInvitedAt && (
                <span className="text-xs text-muted-foreground">
                  Last invited {formatDateTime(merchant.lastInvitedAt)}
                </span>
              )}
              <button
                onClick={() => resendInvite.mutate()}
                disabled={resendInvite.isPending}
                className="px-3 py-1.5 border border-amber-300 text-amber-700 bg-amber-50 rounded-md text-sm font-medium hover:bg-amber-100 disabled:opacity-50"
              >
                {resendInvite.isPending ? 'Sending…' : 'Resend Invite'}
              </button>
            </div>
          )}
        </div>
        {users.length === 0 ? (
          <p className="text-sm text-foreground">No users assigned to this merchant yet.</p>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border">
                <th className="text-left py-2 font-semibold text-foreground">Name</th>
                <th className="text-left py-2 font-semibold text-foreground">Email</th>
                <th className="text-left py-2 font-semibold text-foreground">Phone</th>
                <th className="text-left py-2 font-semibold text-foreground">Role</th>
                <th className="text-left py-2 font-semibold text-foreground">Verified</th>
                <th className="text-left py-2 font-semibold text-foreground">Profile</th>
                <th className="text-left py-2 font-semibold text-foreground">Joined</th>
                {canUpdateUser && <th className="text-right py-2 font-semibold text-foreground">Actions</th>}
              </tr>
            </thead>
            <tbody>
              {users.map((user) => (
                <tr key={user.id} className="border-b border-border">
                  <td className="py-2 font-medium text-foreground">{user.fullName}</td>
                  <td className="py-2 text-foreground">{user.email}</td>
                  <td className="py-2 text-foreground">{user.phoneNumber ?? '—'}</td>
                  <td className="py-2 text-foreground">{user.role.name.replace('_', ' ')}</td>
                  <td className="py-2">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${user.isEmailVerified ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
                      {user.isEmailVerified ? 'Yes' : 'Pending'}
                    </span>
                  </td>
                  <td className="py-2">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${user.isProfileCompleted ? 'bg-green-100 text-green-800' : 'bg-muted text-muted-foreground'}`}>
                      {user.isProfileCompleted ? 'Complete' : 'Incomplete'}
                    </span>
                  </td>
                  <td className="py-2 text-foreground">{formatDate(user.createdAt)}</td>
                  {canUpdateUser && (
                    <td className="py-2 text-right">
                      <button
                        onClick={() => setEditingUser(user)}
                        className="px-2.5 py-1 border border-border rounded-md text-xs font-medium text-foreground hover:bg-muted"
                      >
                        Edit
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Merchant Tools — per-merchant surfaces (catalogue metadata, bookable services,
          custom orders). These are their own pages scoped to a "current merchant"; a
          COLLECTIVE admin reaches them from here (merchant users have them directly in
          their own sidebar). Each row pins THIS merchant then opens the page. */}
      {(() => {
        const tools = [
          {
            label: 'Product Metadata',
            description: "Categories, sizes, colours, materials and size guides for this merchant's catalogue.",
            path: '/product-metadata',
            show: hasPermission(Permission.METADATA_VIEW),
          },
          {
            label: 'Booking Services',
            description: 'Bookable services customers can schedule, and whether they are charged.',
            path: '/booking-services',
            show: hasPermission(Permission.BOOKING_SERVICE_VIEW),
          },
          {
            label: 'Custom Orders',
            description: 'Bespoke/made-to-order requests submitted to this merchant.',
            path: '/custom-orders',
            show: hasPermission(Permission.ORDER_READ),
          },
          {
            label: 'Homepage Slider',
            description: "Slides shown on this merchant's storefront homepage.",
            path: '/homepage-slider',
            show: hasPermission(Permission.SLIDER_VIEW),
          },
          {
            label: 'Payouts',
            description: 'Earnings summary, payout destination and settlement history.',
            path: '/payouts',
            show: hasPermission(Permission.SETTLEMENT_VIEW),
          },
          {
            label: 'Instagram Connections',
            description:
              'Platform-wide Instagram connection health, and the reset action for a stuck token.',
            // NOT '/settings/instagram': watchman resolves that page's `me` routes from the
            // JWT, which a COLLECTIVE session has no merchantId on — pinning the merchant
            // cannot help. The oversight list is the staff-side equivalent.
            path: '/instagram/connections',
            show: hasPermission(Permission.MERCHANT_VIEW),
          },
        ].filter((t) => t.show);
        if (tools.length === 0) return null;
        return (
          <div className="bg-card rounded-lg border border-border p-6">
            <h2 className="text-base font-semibold text-foreground mb-1">Merchant Tools</h2>
            <p className="text-sm text-muted-foreground mb-4">Manage this merchant's catalogue and services.</p>
            <div className="divide-y divide-border">
              {tools.map((tool) => (
                <div key={tool.path} className="flex items-center justify-between py-3 first:pt-0 last:pb-0">
                  <div className="pr-4">
                    <div className="text-sm font-medium text-foreground">{tool.label}</div>
                    <p className="text-sm text-muted-foreground mt-0.5">{tool.description}</p>
                  </div>
                  <Button variant="outline" size="sm" onClick={() => manageForMerchant(tool.path)}>
                    Manage
                  </Button>
                </div>
              ))}
            </div>
          </div>
        );
      })()}

      {/* Intake Field Builder */}
      <div className="bg-card rounded-lg border border-border p-6">
        <div className="mb-4">
          <h2 className="text-base font-semibold text-foreground">Intake Field Configuration</h2>
          <p className="text-sm text-foreground mt-0.5">
            Customise the fields customers fill in for Booking, Custom Order, and Package services.
          </p>
        </div>
        {merchant.solutionId ? (
          <DomainFieldBuilder merchantId={merchant.id} solutionId={merchant.solutionId} />
        ) : (
          <p className="text-sm text-foreground">No solution ID assigned to this merchant — field configuration is unavailable.</p>
        )}
      </div>

      {showEdit && <EditMerchantForm merchant={merchant} onClose={() => setShowEdit(false)} />}
      {newKey && <ApiKeyModal apiKey={newKey} onClose={() => setNewKey(null)} />}
      {editingUser && <EditUserModal user={editingUser} merchantId={merchant.id} onClose={() => setEditingUser(null)} />}

      {showStatusConfirm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-card rounded-lg w-full max-w-md">
            <div className="p-6 border-b">
              <h2 className="text-xl font-bold text-foreground">
                {merchant.active ? 'Deactivate Merchant' : 'Activate Merchant'}
              </h2>
            </div>
            <div className="p-6 space-y-4">
              <p className="text-sm text-foreground">
                {merchant.active
                  ? `This will deactivate ${merchant.name}. Their storefront and API access will be suspended until reactivated.`
                  : `This will reactivate ${merchant.name}, restoring their storefront and API access.`}
              </p>
              <div className="flex gap-3 justify-end">
                <button
                  onClick={() => setShowStatusConfirm(false)}
                  className="px-4 py-2 border border-border rounded-md text-sm text-foreground hover:bg-muted"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    toggleStatus.mutate({ id: merchant.id, active: merchant.active }, { onSuccess: () => setShowStatusConfirm(false) });
                  }}
                  disabled={toggleStatus.isPending}
                  className={`px-4 py-2 rounded-md text-sm font-medium text-white disabled:opacity-50 ${merchant.active ? 'bg-red-600 hover:bg-red-700' : 'bg-green-600 hover:bg-green-700'}`}
                >
                  {toggleStatus.isPending ? 'Working…' : merchant.active ? 'Yes, deactivate' : 'Yes, activate'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-card rounded-lg w-full max-w-md">
            <div className="p-6 border-b">
              <h2 className="text-xl font-bold text-foreground">Delete Merchant Permanently</h2>
            </div>
            <div className="p-6 space-y-4">
              <div className="bg-red-50 border border-red-200 rounded-md p-3 text-sm text-red-800">
                This will permanently delete <strong>{merchant.name}</strong> and all associated data — users, customers, products, orders, and API keys. This cannot be undone.
              </div>
              <div className="flex gap-3 justify-end">
                <button
                  onClick={() => setShowDeleteConfirm(false)}
                  className="px-4 py-2 border border-border rounded-md text-sm text-foreground hover:bg-muted"
                >
                  Cancel
                </button>
                <button
                  onClick={() => deleteMerchant.mutate(merchant.id, { onSuccess: () => navigate('/merchants') })}
                  disabled={deleteMerchant.isPending}
                  className="px-4 py-2 bg-red-600 text-white rounded-md text-sm font-medium hover:bg-red-700 disabled:opacity-50"
                >
                  {deleteMerchant.isPending ? 'Deleting…' : 'Delete Permanently'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
